import { lazy, Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { Toaster } from '@/components/ui';
import {
  DefaultLayout,
  DashboardLayout,
  ProfileLayout,
  RequireAuth,
} from '@/components/layout';
import { pageview as analyticsPageview } from '@/lib/analytics';
// Dev-only Phase-0 design system showcase — `lazy()` so the demo never
// ships in the main bundle and the route registration below also gates
// on `import.meta.env.DEV` so it isn't reachable in production builds.
const Foundation = lazy(() =>
  import('@/pages/_foundation/Foundation').then((m) => ({ default: m.Foundation })),
);

// Public landing + static content + 404 (Phase 1 #2). Lazy-loaded so the
// marketing pages don't weigh down the initial dashboard bundle.
const Home = lazy(() => import('@/pages/home/Home'));
const Login = lazy(() => import('@/pages/auth/Login'));
const Register = lazy(() => import('@/pages/auth/Register'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const Dashboard = lazy(() => import('@/pages/timeline/Dashboard'));
const About = lazy(() => import('@/pages/legal/About'));
const Privacy = lazy(() => import('@/pages/legal/Privacy'));
const Terms = lazy(() => import('@/pages/legal/Terms'));
const Refund = lazy(() => import('@/pages/legal/Refund'));
const Contact = lazy(() => import('@/pages/legal/Contact'));
const NotFound = lazy(() => import('@/pages/_notfound/NotFound'));
const Profile = lazy(() => import('@/pages/profile/Profile'));
const Friends = lazy(() => import('@/pages/friends/Friends'));
const FriendRequests = lazy(() => import('@/pages/friends/FriendRequests'));
const Visitors = lazy(() => import('@/pages/friends/Visitors'));
const Messages = lazy(() => import('@/pages/chat/Messages'));
const Blogs = lazy(() => import('@/pages/blogs/Blogs'));
const BlogDetail = lazy(() => import('@/pages/blogs/BlogDetail'));
const MyBlogs = lazy(() => import('@/pages/blogs/MyBlogs'));
const News = lazy(() => import('@/pages/news/News'));
const NewsArticle = lazy(() => import('@/pages/news/NewsArticle'));
const LiveTVHome = lazy(() => import('@/pages/livetv/LiveTVHome'));
const LiveTVCategories = lazy(() => import('@/pages/livetv/LiveTVCategories'));
const LiveTVCountries = lazy(() => import('@/pages/livetv/LiveTVCountries'));
const LiveTVLanguages = lazy(() => import('@/pages/livetv/LiveTVLanguages'));
const LiveTVBrowse = lazy(() => import('@/pages/livetv/LiveTVBrowse'));
const LiveTVWatch = lazy(() => import('@/pages/livetv/LiveTVWatch'));
const Livestream = lazy(() => import('@/pages/livestream/Livestream'));
const Places = lazy(() => import('@/pages/places/Places'));
const People = lazy(() => import('@/pages/people/People'));
const EditProfile = lazy(() => import('@/pages/account/EditProfile'));
const Settings = lazy(() => import('@/pages/account/Settings'));
const Notifications = lazy(() => import('@/pages/notifications/Notifications'));
const InviteRedirect = lazy(() => import('@/pages/invite/InviteRedirect'));
const InviteFriends = lazy(() => import('@/pages/invite/InviteFriends'));
const Pricing = lazy(() => import('@/pages/pricing/Pricing'));
// Local Events at Places (Phase 1 MVP). `EventDetail` is a PUBLIC page
// (Node emits schema.org/Event JSON-LD for it via WebController.event) —
// it's rendered under DefaultLayout, not RequireAuth. Create + MyEvents
// are auth-gated under DashboardLayout below.
const EventDetail = lazy(() => import('@/pages/events/EventDetail'));
const EventsDiscovery = lazy(() => import('@/pages/events/EventsDiscovery'));
const CreateEvent = lazy(() => import('@/pages/events/CreateEvent'));
const MyEvents = lazy(() => import('@/pages/events/MyEvents'));
// Cities — public SEO + discovery pages (/cities, /cities/:country,
// /city/:country/:slug). Node WebController emits the HTML shell with
// Place + BreadcrumbList JSON-LD; the SPA hydrates the interactive panels.
const CityIndex = lazy(() => import('@/pages/city/CityIndex'));
const CityDetail = lazy(() => import('@/pages/city/CityDetail'));
// Legacy `/city/:slug` single-segment URL — resolves the top-ranked
// (country_slug, slug) match client-side and redirects to the canonical
// `/city/:country/:slug`. Mirrors `WebController.cityLegacy` for dev,
// where Vite serves the SPA shell before the Node 301 can fire.
const CityLegacyRedirect = lazy(() => import('@/pages/city/CityLegacyRedirect'));
// Yaariplay — daily word game (Phase 1 MVP). PUBLIC route: signed-out
// visitors can view the leaderboard + stats. The guess CTA redirects to
// /login?next=... when the viewer is anonymous.
const YaariplayPage = lazy(() => import('@/pages/play/YaariplayPage'));
// Public Pages-CMS renderer — paints the body Laravel injects via
// `window.__SSR_PAGE__`. Source-of-truth = Laravel IndexController.
const PageView = lazy(() => import('@/pages/page/PageView'));

/**
 * Suspense fallback shown while a lazy-loaded route chunk is fetching.
 *
 * Visually matches the pre-React boot loader in `index.html` (same brand-blue
 * 48px spinner + "Yaarikut" wordmark + 0.75s spin) so a cold load and an
 * in-app route transition feel like the same experience.
 *
 * Key differences from the boot loader:
 *   - NOT `position: fixed inset: 0` — the chrome (Topbar, Sidebar, etc.)
 *     is already mounted by this point, so a full-screen overlay would
 *     flash over them. We fill the route's content area instead.
 *   - No background fill — the parent layout's surface shows through.
 *
 * The literal hex `#33b0f2` matches the boot loader exactly (and is the
 * brand-500 token across the app) so a swap from one to the other is
 * imperceptible.
 */
function PageFallback() {
  // min-h-screen so the lazy-route placeholder reserves at least the full
  // viewport while the chunk is fetching. Most loaded pages are TALLER than
  // the viewport, so this prevents the page below from collapsing under
  // 60vh and then jumping back up when the chunk lands (CLS win on cold
  // load + route transitions). Trade-off: short routes (NotFound, Login)
  // will shift the footer up on first paint, but those are rare hits.
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-[18px] py-10"
      role="status"
      aria-label="Loading"
    >
      <div
        className="h-12 w-12 animate-spin rounded-full border-4 border-[rgba(51,176,242,0.18)] border-t-[#33b0f2] [animation-duration:0.75s]"
        aria-hidden="true"
      />
      <div className="text-[18px] font-semibold tracking-[0.04em] text-ink">
        Yaarikut
      </div>
    </div>
  );
}

// Legacy redirect shims for the prior `/live-tv/*` ↔ `/tv/*` rename are
// removed. The active route shape is now `/live-tv/*` again (see Routes
// below). Any old indexed `/tv/*` URLs fall through to the global 404
// handler so search engines de-index them naturally.

/**
 * Reset scroll to top on every PUSH/REPLACE navigation. Without it,
 * navigating from the friends grid into a profile (or any list →
 * detail link) leaves the page scrolled wherever the list was —
 * the new page starts mid-screen with its header chopped off above.
 *
 * Skipped on:
 *   - POP (browser back/forward): the browser already restores the
 *     prior scroll position; overriding it would lose context.
 *   - hash navigations (`/page#section`): the browser is honouring
 *     an anchor target — don't fight it.
 *   - same-pathname re-navigation (clicking the active tab again):
 *     would be jarring for the user.
 */
function ScrollToTop() {
  const { pathname, hash, state } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType === 'POP') return;
    if (hash) return;
    // Opt-out: callers can pass `state={{ preventScrollReset: true }}`
    // on their `<Link>` (or `navigate(..., { state })`) when the
    // navigation is meant to feel like a tab switch — e.g. the
    // profile tab bar (Timeline / About / Friends / Media / Blogs)
    // changes the URL but the user expects to stay scrolled where
    // they were, not have the page jump back to the cover image.
    if (state && typeof state === 'object' && 'preventScrollReset' in state &&
        (state as { preventScrollReset?: unknown }).preventScrollReset) {
      return;
    }
    // Two `requestAnimationFrame`s guarantee one paint between
    // React's commit and the scroll — without the defer, the
    // animation starts using the old (often taller) document
    // height and may land mid-page when the new layout shrinks.
    const cancelHandles: { r2: number | null; tabsTimer: number | null } = {
      r2: null,
      tabsTimer: null,
    };
    // Profile is `lazy()`-loaded under <Suspense>. On a cold load /
    // refresh / share-link tap, the chunk is still being fetched
    // when the rAF cycle fires — `<nav aria-label="Profile
    // sections">` doesn't exist yet, and the previous implementation
    // silently fell through to scroll-to-top (the exact opposite of
    // the intended deep-link tab anchor). Poll for the tabs element
    // with a short retry window so cold loads ALSO land at the tab
    // bar. Cap at ~700ms so a genuinely missing nav doesn't hang
    // the scroll forever.
    function scrollDeepLinkOrTop(retriesLeft: number) {
      const isActivityDeepLink =
        /^\/user\/[^/]+\/activity\/id\/\d+/.test(pathname);
      // Use Tailwind's `lg` boundary (1024 → match below = ≤1023.98)
      // so the deep-link anchor and CSS-driven sticky-rail / tabs
      // collapse cross the same viewport line.
      if (isActivityDeepLink && window.innerWidth <= 1023.98) {
        const tabs = document.querySelector(
          'nav[aria-label="Profile sections"]',
        );
        if (tabs) {
          const TOPBAR_OFFSET = 64;
          const targetY =
            tabs.getBoundingClientRect().top +
            window.scrollY -
            TOPBAR_OFFSET;
          window.scrollTo({
            top: Math.max(0, targetY),
            left: 0,
            behavior: 'smooth',
          });
          return;
        }
        if (retriesLeft > 0) {
          // Retry after Suspense has had a chance to resolve the
          // Profile chunk. 100ms × 7 = ~700ms total budget — enough
          // for a typical async chunk fetch on a warm dev server or
          // any reasonable production CDN.
          cancelHandles.tabsTimer = window.setTimeout(
            () => scrollDeepLinkOrTop(retriesLeft - 1),
            100,
          );
          return;
        }
        // Budget exhausted — fall back to scroll-to-top so the page
        // is at least in a sane resting position.
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => {
        scrollDeepLinkOrTop(7);
      });
      cancelHandles.r2 = r2;
    });
    return () => {
      cancelAnimationFrame(r1);
      if (cancelHandles.r2 != null) cancelAnimationFrame(cancelHandles.r2);
      if (cancelHandles.tabsTimer != null) {
        clearTimeout(cancelHandles.tabsTimer);
      }
    };
  }, [pathname, hash, navigationType, state]);
  return null;
}

/**
 * Auto-emit `pageview()` on every navigation. The facade itself dedupes
 * routes that fire their own dedicated view event (profile / blog detail /
 * news article / channel page) via `_pageViewSuppressed`, so per-page
 * `pageview()` calls become a backstop for pages that opted in early —
 * the next navigation here always covers it. Title is read AFTER an
 * `requestAnimationFrame` so `react-helmet-async` has had a chance to
 * update `document.title` for the new route.
 */
function PageviewTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const path = pathname + (search || '');
    const handle = requestAnimationFrame(() => {
      analyticsPageview(path, document.title);
    });
    return () => cancelAnimationFrame(handle);
  }, [pathname, search]);
  return null;
}

/**
 * Phase 1 #1 router — full §6 route inventory wired below the correct layout
 * shells (URL parity with the old site). Every leaf currently renders a
 * `Placeholder`; real pages replace these element-by-element in later steps.
 *
 * `RequireAuth` wraps the dashboard shell but is a pass-through for now.
 */
export function AppRouter() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <PageviewTracker />
      <Routes>
        {/* Public site */}
        <Route element={<DefaultLayout />}>
          <Route
            path="/"
            element={
              <Suspense fallback={<PageFallback />}>
                <Home />
              </Suspense>
            }
          />
          <Route
            path="/about.html"
            element={
              <Suspense fallback={<PageFallback />}>
                <About />
              </Suspense>
            }
          />
          <Route
            path="/privacy-policy.html"
            element={
              <Suspense fallback={<PageFallback />}>
                <Privacy />
              </Suspense>
            }
          />
          <Route
            path="/terms-conditions.html"
            element={
              <Suspense fallback={<PageFallback />}>
                <Terms />
              </Suspense>
            }
          />
          <Route
            path="/refund-policy.html"
            element={
              <Suspense fallback={<PageFallback />}>
                <Refund />
              </Suspense>
            }
          />
          <Route
            path="/contact-us.html"
            element={
              <Suspense fallback={<PageFallback />}>
                <Contact />
              </Suspense>
            }
          />
          <Route
            path="/pricing.html"
            element={
              <Suspense fallback={<PageFallback />}>
                <Pricing />
              </Suspense>
            }
          />

          {/* Pages CMS — admin-authored slugs (privacy-policy, terms,
              contact-us, …). Laravel injects the body via
              `window.__SSR_PAGE__`; the React renderer just paints it.
              No client-side fetch (the SSR payload is the only source). */}
          <Route
            path="/page/:slug"
            element={
              <Suspense fallback={<PageFallback />}>
                <PageView />
              </Suspense>
            }
          />

          <Route
            path="/blogs"
            element={
              <Suspense fallback={<PageFallback />}>
                <Blogs />
              </Suspense>
            }
          />
          <Route
            path="/blogs/:category"
            element={
              <Suspense fallback={<PageFallback />}>
                <Blogs />
              </Suspense>
            }
          />
          <Route
            path="/blogs/:category/:slug"
            element={
              <Suspense fallback={<PageFallback />}>
                <BlogDetail />
              </Suspense>
            }
          />

          <Route
            path="/news"
            element={
              <Suspense fallback={<PageFallback />}>
                <News />
              </Suspense>
            }
          />
          <Route
            path="/news/:country"
            element={
              <Suspense fallback={<PageFallback />}>
                <News />
              </Suspense>
            }
          />
          <Route
            path="/news/:country/:slug"
            element={
              <Suspense fallback={<PageFallback />}>
                <NewsArticle />
              </Suspense>
            }
          />

          <Route
            path="/invite/:code"
            element={
              <Suspense fallback={<PageFallback />}>
                <InviteRedirect />
              </Suspense>
            }
          />

          {/* Public event detail — Node emits the schema.org/Event JSON-LD
              shell at /event/:id (see WebController.event), and the SPA
              hydrates on top with the interactive RSVP row + attendee grid. */}
          <Route
            // Route param MUST be `:idSlug` — EventDetail.tsx parses the
            // numeric prefix off `<id>-<slug>` via useParams<{ idSlug }>.
            // Using `:id` here left `idSlug` undefined → useEvent(undefined)
            // never fired → the "Event not found" branch rendered even
            // though the server returned 200 with valid data.
            path="/event/:idSlug"
            element={
              <Suspense fallback={<PageFallback />}>
                <EventDetail />
              </Suspense>
            }
          />

          {/* Public events discovery — SEO-critical. Node WebController.
              eventsIndex emits the ItemList JSON-LD shell; React hydrates
              with filters, infinite scroll, and the map view toggle. */}
          <Route
            path="/events"
            element={
              <Suspense fallback={<PageFallback />}>
                <EventsDiscovery />
              </Suspense>
            }
          />
          {/* /events/<category> — convenience URL that opens the discover
              page with the given category chip pre-selected. The slug is
              validated client-side against CATEGORY_VOCAB; unknown values
              fall through to "All categories". */}
          <Route
            path="/events/:categorySlug"
            element={
              <Suspense fallback={<PageFallback />}>
                <EventsDiscovery />
              </Suspense>
            }
          />

          {/* Cities directory — /cities and country-filtered /cities/:country.
              Node WebController.citiesIndex emits the ItemList JSON-LD shell;
              this page hydrates with the interactive grid. */}
          <Route
            path="/cities"
            element={
              <Suspense fallback={<PageFallback />}>
                <CityIndex />
              </Suspense>
            }
          />
          <Route
            path="/cities/:country"
            element={
              <Suspense fallback={<PageFallback />}>
                <CityIndex />
              </Suspense>
            }
          />

          {/* City detail — canonical /city/:countrySlug/:citySlug. Server
              SSR emits Place + BreadcrumbList JSON-LD + a crawler stub
              with the H1 and first-page events list. */}
          <Route
            path="/city/:countrySlug/:citySlug"
            element={
              <Suspense fallback={<PageFallback />}>
                <CityDetail />
              </Suspense>
            }
          />

          {/* Legacy /city/:slug — one-segment URL. Routed AFTER the
              two-segment canonical form so React Router prefers the
              richer match when both segments are present. Hits the
              backend /cities/resolve/:slug and navigates to the
              canonical URL. 404 if the slug has no match. */}
          <Route
            path="/city/:citySlug"
            element={
              <Suspense fallback={<PageFallback />}>
                <CityLegacyRedirect />
              </Suspense>
            }
          />

          {/* Yaariplay — daily word game (Phase 1 MVP). Registered PUBLIC
              so signed-out visitors can view the leaderboard + stats; the
              guess CTA redirects to /login?next=/play/yaariplay when the
              viewer isn't authenticated. */}
          <Route
            path="/play/yaariplay"
            element={
              <Suspense fallback={<PageFallback />}>
                <YaariplayPage />
              </Suspense>
            }
          />

          {/* Channels — main landing, drill-in listings, channel-grid filters,
              and the player page. All `/live-tv*` routes are gated with a 404
              fallback (not a redirect) for unauthenticated visitors: from a
              search crawler / payment gateway underwriter POV the route looks
              like it doesn't exist on the site at all. Authenticated members
              still get the full experience. The Suspense is hoisted OUTSIDE
              RequireAuth so it catches both the protected child AND the lazy
              <NotFound/> when it's served as the anon fallback.

              The prior `/tv/*` shape was retired (2026-06-18) so Google's
              older indexed `/tv/*` URLs naturally 404 and drop out of the
              index — they aren't being redirected to the new shape since
              a redirect would keep the index pointing at us. */}
          <Route
            path="/live-tv"
            element={
              <Suspense fallback={<PageFallback />}>
                <RequireAuth fallback={<NotFound />}>
                  <LiveTVHome />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/live-tv/categories"
            element={
              <Suspense fallback={<PageFallback />}>
                <RequireAuth fallback={<NotFound />}>
                  <LiveTVCategories />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/live-tv/countries"
            element={
              <Suspense fallback={<PageFallback />}>
                <RequireAuth fallback={<NotFound />}>
                  <LiveTVCountries />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/live-tv/languages"
            element={
              <Suspense fallback={<PageFallback />}>
                <RequireAuth fallback={<NotFound />}>
                  <LiveTVLanguages />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/live-tv/category/:slug"
            element={
              <Suspense fallback={<PageFallback />}>
                <RequireAuth fallback={<NotFound />}>
                  <LiveTVBrowse facet="category" />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/live-tv/country/:code"
            element={
              <Suspense fallback={<PageFallback />}>
                <RequireAuth fallback={<NotFound />}>
                  <LiveTVBrowse facet="country" />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/live-tv/language/:code"
            element={
              <Suspense fallback={<PageFallback />}>
                <RequireAuth fallback={<NotFound />}>
                  <LiveTVBrowse facet="language" />
                </RequireAuth>
              </Suspense>
            }
          />
          <Route
            path="/live-tv/watch/:channelId"
            element={
              <Suspense fallback={<PageFallback />}>
                <RequireAuth fallback={<NotFound />}>
                  <LiveTVWatch />
                </RequireAuth>
              </Suspense>
            }
          />

          <Route
            path="/404"
            element={
              <Suspense fallback={<PageFallback />}>
                <NotFound />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense fallback={<PageFallback />}>
                <NotFound />
              </Suspense>
            }
          />
        </Route>

        {/* Authenticated dashboard */}
        <Route
          element={
            <RequireAuth>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route
            path="/dashboard"
            element={
              <Suspense fallback={<PageFallback />}>
                <Dashboard />
              </Suspense>
            }
          />
          <Route
            path="/hashtag/:tag"
            element={
              <Suspense fallback={<PageFallback />}>
                <Dashboard />
              </Suspense>
            }
          />
          <Route
            path="/profile/edit"
            element={
              <Suspense fallback={<PageFallback />}>
                <EditProfile />
              </Suspense>
            }
          />
          <Route
            path="/profile/edit/:tab"
            element={
              <Suspense fallback={<PageFallback />}>
                <EditProfile />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/messages"
            element={
              <Suspense fallback={<PageFallback />}>
                <Messages />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/messages/:username"
            element={
              <Suspense fallback={<PageFallback />}>
                <Messages />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/friends"
            element={
              <Suspense fallback={<PageFallback />}>
                <Friends />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/friend-requests"
            element={
              <Suspense fallback={<PageFallback />}>
                <FriendRequests />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/visitors"
            element={
              <Suspense fallback={<PageFallback />}>
                <Visitors />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/blogs"
            element={
              <Suspense fallback={<PageFallback />}>
                <MyBlogs />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/notifications"
            element={
              <Suspense fallback={<PageFallback />}>
                <Notifications />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <Suspense fallback={<PageFallback />}>
                <Settings />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/settings/:tab"
            element={
              <Suspense fallback={<PageFallback />}>
                <Settings />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/invite-friends"
            element={
              <Suspense fallback={<PageFallback />}>
                <InviteFriends />
              </Suspense>
            }
          />
          <Route
            path="/dashboard/livestream"
            element={
              <Suspense fallback={<PageFallback />}>
                <Livestream />
              </Suspense>
            }
          />
          <Route
            path="/users"
            element={
              <Suspense fallback={<PageFallback />}>
                <People />
              </Suspense>
            }
          />
          <Route
            path="/users/search/:type"
            element={
              <Suspense fallback={<PageFallback />}>
                <People />
              </Suspense>
            }
          />
          <Route
            path="/users/search/:type/:value"
            element={
              <Suspense fallback={<PageFallback />}>
                <People />
              </Suspense>
            }
          />
          <Route
            path="/places"
            element={
              <Suspense fallback={<PageFallback />}>
                <Places />
              </Suspense>
            }
          />

          {/* Events (Phase 1 MVP) — create + my-events under dashboard chrome.
              Detail is public (see the /event/:id route under DefaultLayout). */}
          <Route
            path="/events/create"
            element={
              <Suspense fallback={<PageFallback />}>
                <CreateEvent />
              </Suspense>
            }
          />
          <Route
            path="/events/mine"
            element={
              <Suspense fallback={<PageFallback />}>
                <MyEvents />
              </Suspense>
            }
          />
        </Route>

        {/* Public profiles (dashboard chrome when signed in) */}
        <Route element={<ProfileLayout />}>
          <Route
            path="/user/:username"
            element={
              <Suspense fallback={<PageFallback />}>
                <Profile />
              </Suspense>
            }
          />
          <Route
            path="/user/:username/:page"
            element={
              <Suspense fallback={<PageFallback />}>
                <Profile />
              </Suspense>
            }
          />
          <Route
            path="/user/:username/:page/:id"
            element={
              <Suspense fallback={<PageFallback />}>
                <Profile />
              </Suspense>
            }
          />
          <Route
            path="/user/:username/:page/id/:id"
            element={
              <Suspense fallback={<PageFallback />}>
                <Profile />
              </Suspense>
            }
          />
        </Route>

        {/* Auth — full-screen split-screen (self-contained AuthLayout), no public nav/footer */}
        <Route
          path="/login"
          element={
            <Suspense fallback={<PageFallback />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/register"
          element={
            <Suspense fallback={<PageFallback />}>
              <Register />
            </Suspense>
          }
        />
        <Route
          path="/forgotpassword"
          element={
            <Suspense fallback={<PageFallback />}>
              <ForgotPassword />
            </Suspense>
          }
        />

        {/* Dev-only: Phase 0 foundation showcase (no layout chrome).
            The route is not registered in production builds — and the
            module is lazy-imported above so it isn't bundled either. */}
        {import.meta.env.DEV ? (
          <Route
            path="/_foundation"
            element={
              <Suspense fallback={<PageFallback />}>
                <Foundation />
              </Suspense>
            }
          />
        ) : null}
      </Routes>
      {/* App-wide toast viewport. Mounted INSIDE BrowserRouter so any
          toast containing a `<Link>` / `useNavigate` (e.g. the
          LiveNotifier's "Watch" CTA) has the Router context it needs. */}
      <Toaster />
    </BrowserRouter>
  );
}
